import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ArrowLeft, Loader2, CheckCircle2, XCircle, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Link non valido: token mancante. Richiedi un nuovo link dal login.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    if (password !== confirm) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password aggiornata! Puoi ora effettuare il login.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante il reset. Il link potrebbe essere scaduto.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "radial-gradient(ellipse at top, #143830 0%, #0A1F18 100%)" }}>
      <div className="w-full max-w-md">
        <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono text-sheriff-gold/70 mb-2">
              <KeyRound className="h-3 w-3" /> Reset password
            </div>
            <h1 className="font-heading font-black text-3xl text-[#F5E6B8] tracking-tight" data-testid="reset-password-title">
              Nuova Password
            </h1>
            <p className="text-sm text-[#B8A269] mt-2">
              Scegli una password sicura per il tuo account LSSD.
            </p>
          </div>

          {success ? (
            <div className="text-center py-6 space-y-4" data-testid="reset-success">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/40">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
              <div>
                <div className="font-heading font-bold text-lg text-[#F5E6B8] mb-2">Password aggiornata!</div>
                <p className="text-sm text-[#B8A269]">Verrai reindirizzato al login…</p>
              </div>
              <Link to="/login" className="text-sheriff-gold hover:text-[#F5E6B8] text-sm underline">
                Vai al login ora
              </Link>
            </div>
          ) : error && !token ? (
            <div className="text-center py-6 space-y-4" data-testid="reset-error">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-rose-500/15 border border-rose-500/40">
                <XCircle className="h-7 w-7 text-rose-400" />
              </div>
              <p className="text-sm text-rose-300">{error}</p>
              <Link to="/forgot-password" className="inline-flex items-center gap-1 text-sheriff-gold hover:text-[#F5E6B8] text-sm">
                <ShieldAlert className="h-3 w-3" /> Richiedi nuovo link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label className="text-xs font-semibold text-[#E8D89A] mb-1.5 block">Nuova password</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sheriff-gold/50" />
                  <Input
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Almeno 6 caratteri"
                    required
                    autoFocus
                    minLength={6}
                    className="pl-10 pr-10 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sheriff-gold/50 hover:text-sheriff-gold"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#E8D89A] mb-1.5 block">Conferma password</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sheriff-gold/50" />
                  <Input
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Ripeti la password"
                    required
                    minLength={6}
                    className="pl-10 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
                    data-testid="reset-confirm-password"
                  />
                </div>
              </div>
              {error && (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full bg-sheriff-gold text-[#0A1F18] hover:bg-sheriff-gold/90 font-bold"
                data-testid="reset-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Reimposta password
              </Button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-xs text-sheriff-gold/70 hover:text-sheriff-gold inline-flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" /> Torna al login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
